import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useMemo, useCallback, useEffect } from 'react';

import PublicOfficeApp from 'hb-react/publicoffice/providers/App';
import getSelectComponent from 'hb-react/shared/components/formik/utils/getSelectComponent';
import { getOptionValue } from 'hb-react/shared/components/ReactSelect/utils/selectValues';

const ReactSelect = ({
  type,
  disabled,
  id,
  name,
  defaultValue,
  options,
  placeholder,
  multiple,
  creatable,
  isClearable,
  isNotSearchable,
  notCloseMenuOnSelect,
  ariaLabel,
  ariaDescribedBy,
  onChange,
  onBlur,
  onCreateOption,
  onMount,
  ...otherProps
}) => {
  const [value, setValue] = useState(defaultValue);
  const SelectComponent = useMemo(() => getSelectComponent(type), [type]);

  // If options is a ruby options list, serialize it in proper format for react-select.
  const optionsList = useMemo(() => {
    if (options && Array.isArray(options)) {
      return options.map((option) => {
        if (Array.isArray(option)) {
          const [name, id] = option;

          return {
            id,
            name,
          };
        }

        return option;
      });
    }

    return options;
  }, [options]);

  // Handlers
  const handleBlur = useCallback(
    (selectedOptions) => onBlur?.(selectedOptions),
    [onBlur],
  );

  const handleChange = useCallback(
    (selectedOptions) => {
      let newValue;

      if (selectedOptions) {
        if (multiple && Array.isArray(selectedOptions)) {
          newValue = selectedOptions.map((option) => getOptionValue(option));
        } else {
          newValue = getOptionValue(selectedOptions);
        }
      }

      setValue(newValue);
      onChange?.(selectedOptions);
    },
    [onChange],
  );

  useEffect(() => onMount?.(), [onMount]);

  return (
    <PublicOfficeApp network={get(window, 'AlumniConnect.currentNetwork', {})}>
      <SelectComponent
        disabled={disabled}
        id={id}
        isClearable={isClearable}
        isNotSearchable={isNotSearchable}
        isMulti={multiple}
        creatable={creatable}
        name={name}
        notCloseMenuOnSelect={notCloseMenuOnSelect}
        onBlur={handleBlur}
        onChange={handleChange}
        options={optionsList}
        placeholder={placeholder}
        value={value}
        ariaLabel={ariaLabel}
        ariaDescribedBy={ariaDescribedBy}
        onCreateOption={onCreateOption}
        {...otherProps}
      />
    </PublicOfficeApp>
  );
};

ReactSelect.propTypes = {
  type: PropTypes.oneOf([
    'companies',
    'countries',
    'default',
    'degrees',
    'field_of_studies',
    'industries',
    'job_functions',
    'nationalities',
    'schools',
    'skills',
    'sub_networks',
    'timezone',
    'customizable_attribute_select_async',
  ]),
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  multiple: PropTypes.bool,
  creatable: PropTypes.bool,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  isClearable: PropTypes.bool,
  isNotSearchable: PropTypes.bool,
  notCloseMenuOnSelect: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onCreateOption: PropTypes.func,
  onMount: PropTypes.func,
};

export default ReactSelect;
